import React, { useEffect, useState, useRef } from 'react';
import withLayout from 'hocs/withLayout';
import { PageProps } from 'types/page';
import Tag from './Tag';
import { useRouter } from 'next/router';
import { TagWithPosts } from 'types/tag';
import { useQuery } from '@apollo/client/react';
import { TAG_WITH_POSTS } from 'lib/graphql/queries/tag';
import { PaginationOptions } from '@elevator/ui';
import type { ErrorLike } from '@apollo/client';

/**
 * Tag Layout Wrapper
 *
 * Handles client-side data fetching and state management for tag pages.
 * Initializes state with SSR props to prevent flicker, uses cache-first fetchPolicy
 * to avoid duplicate fetches, and syncs SSR → Client state carefully.
 * Updates query variables when slug changes to fix navigation between tags.
 *
 * Features:
 * - SSR data initialization (prevents flicker)
 * - Cache-first fetchPolicy (avoids duplicate fetches)
 * - Flicker prevention (tracks initial data, skips unnecessary updates)
 * - Slug change handling (resets state, updates query variables)
 * - Loading state management (only shows loading when no data exists)
 *
 * Data Flow:
 * 1. Receives SSR props from getStaticProps
 * 2. Initializes state with SSR data immediately
 * 3. Uses useQuery with cache-first to sync with Apollo cache
 * 4. Updates state only when data actually changes
 * 5. Passes data and handlers to Tag component
 *
 * @param props - Layout props from getStaticProps
 * @param props.tag - Initial tag data from SSR
 * @param props.variables - GraphQL query variables (slug and cursor)
 * @param props.slug - Tag slug identifier
 * @param props.error - GraphQL query error from SSR
 * @returns Tag layout with data management
 */
const Index = (props: TagIndexProps) => {
  const { tag, variables: initialVariables, slug, error, ...restProps } = props;

  const router = useRouter();

  // Get slug from router if not provided (for dynamic routes)
  const finalSlug = slug || String(router.query.slug);

  // Create query variables using current slug (updates on navigation)
  const queryVariables = {
    slug: finalSlug,
    cursor: initialVariables.cursor,
  };

  // Initialize with SSR data immediately (only if slug matches)
  const [tagData, setTagData] = useState(tag && tag.slug === finalSlug ? tag : undefined);
  // Track if we've set initial data to prevent unnecessary updates from cache
  const hasInitialData = useRef(!!tag && tag.slug === finalSlug);
  // Track previous posts length to detect when fetchMore adds new posts
  const prevPostsLengthRef = useRef(
    tag && tag.slug === finalSlug ? tag.posts?.length || 0 : 0,
  );

  // Reset only when navigating to a different slug — not on the initial mount effect run,
  // which previously cleared SSR data and caused the title to flash slug → name.
  const prevSlugForResetRef = useRef(finalSlug);
  useEffect(() => {
    if (prevSlugForResetRef.current === finalSlug) return;
    prevSlugForResetRef.current = finalSlug;
    setTagData(undefined);
    hasInitialData.current = false;
    prevPostsLengthRef.current = 0;
  }, [finalSlug]);

  const {
    data,
    loading,
    networkStatus,
    error: queryError,
    fetchMore,
  } = useQuery<{
    tag: TagWithPosts;
  }>(TAG_WITH_POSTS, {
    fetchPolicy: 'cache-first', // Use cache-first to avoid unnecessary re-fetch
    nextFetchPolicy: 'cache-first', // Keep using cache for subsequent fetches
    variables: queryVariables, // Use updated variables with current slug
    // Enable network status notifications so loadingMore works correctly for fetchMore
    notifyOnNetworkStatusChange: true,
  });

  // Only show loading if we don't have any data yet (not even from SSR/cache)
  // This prevents showing loading state when data is available from cache/SSR
  const isLoading = !tagData && ((loading && networkStatus !== 3) || networkStatus === 4);
  const loadingMore = networkStatus === 3;

  useEffect(() => {
    // Only update state if we have new data AND either:
    // 1. We don't have initial SSR data yet, OR
    // 2. We're actively loading (not just cache resolution), OR
    // 3. Data has actually changed (fetchMore added new posts)
    if (data?.tag) {
      const newPostsLength = data.tag.posts?.length || 0;
      // If we already have initial data and this is just cache resolution (not a real fetch),
      // skip the update to prevent unnecessary re-render that causes image flicker
      // But allow updates when fetchMore adds new posts (length increased)
      if (hasInitialData.current && !loading && networkStatus === 7) {
        // networkStatus 7 = ready (cache resolved, no network request)
        // Check if data actually changed by comparing posts length
        if (prevPostsLengthRef.current === newPostsLength) {
          // Skip update to prevent flicker when cache resolves with same data
          return;
        }
      }
      // Update previous length and state
      prevPostsLengthRef.current = newPostsLength;
      setTagData(data.tag);
      hasInitialData.current = true;
    }
  }, [data, loading, networkStatus]);

  // Use query error if available, otherwise fall back to SSR error
  const finalError = queryError || error;

  return (
    <Tag
      {...restProps}
      tag={tagData}
      slug={finalSlug}
      loading={isLoading}
      loadingMore={loadingMore}
      error={finalError}
      fetchMore={fetchMore}
      variables={queryVariables}
    />
  );
};

export interface TagIndexProps extends PageProps {
  tag?: TagWithPosts;
  variables: {
    slug: string;
    cursor: PaginationOptions;
  };
  slug: string;
  error?: ErrorLike;
}

export default withLayout(Index, { bottomAd: true, margin: true });
